/**
 * @file FontRoleFields.tsx
 * @description Настройки ролей во вкладке «Шрифты» (phase19.16 Р1, Р1-7;
 * SB10, SB73, SB99; uiux-fonts-usage §3, §9 вопрос 3):
 *   - у «Заголовков» — «Начертание» (только те, что есть у шрифта; у своего
 *     незагруженное жирное выключено с подсказкой), «Регистр заголовков» и
 *     «Размер заголовков» (`theme.type.headingScale`, SB10 «Размеры»; SB120 этап 2 —
 *     числом, `HeadingScaleField`);
 *   - у «Основного текста» — «Надписи капсом над заголовками» (`theme.type.
 *     eyebrowFrom`): основным текстом или шрифтом заголовков; при основном —
 *     строка «…— тоже этим шрифтом»; рядом «Разрядка надписей капсом» числом
 *     (`theme.type.eyebrowTracking`, SB120 этап 2, uiux-sb120-stage2 §2).
 * Подписи надписей капсом и размера заголовков — из реестра `@chaiberi/shared`.
 */

import {
  EYEBROW_FROM_LABEL,
  EYEBROW_FROM_NOTE,
  EYEBROW_FROM_OPTIONS,
  type EyebrowFrom,
  type SiteTheme,
} from "@chaiberi/shared";

import { Label } from "../../ui/label";
import { Segmented, type SegmentedOption } from "../Segmented";
import { EyebrowTrackingField, HeadingScaleField } from "./TypeNumberFields";
import type { HeadingWeightChoice } from "../../../lib/site-view/font-weights";

type TypePatch = (patch: Partial<SiteTheme["type"]>) => void;

const CASE_OPTIONS: readonly SegmentedOption<SiteTheme["type"]["headingCase"]>[] = [
  { value: "asis", label: "Как написано" },
  { value: "upper", label: "ПРОПИСНЫЕ" },
];

// Варианты в порядке реестра: новый вариант — запись в shared, не ветка здесь.
const EYEBROW_OPTIONS = (Object.keys(EYEBROW_FROM_OPTIONS) as EyebrowFrom[]).map((value) => ({
  value,
  label: EYEBROW_FROM_OPTIONS[value],
}));

interface HeadingProps {
  weight: HeadingWeightChoice;
  headingCase: SiteTheme["type"]["headingCase"];
  headingScale: SiteTheme["type"]["headingScale"];
  onType: TypePatch;
  /** Живой предпросмотр на канве во время перетаскивания (не запись). */
  onPreview?: TypePatch;
}

/** «Начертание», «Регистр заголовков» и «Размер заголовков». */
export function HeadingTypeFields({ weight, headingCase, headingScale, onType, onPreview }: HeadingProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label>Начертание</Label>
      <Segmented
        aria-label="Начертание заголовков"
        options={weight.options}
        value={String(weight.value)}
        onChange={(value) => onType({ headingWeight: Number(value) })}
      />
      {weight.missingBold ? (
        <p className="type-caption text-on-surface-variant">
          Жирное начертание не загружено. Добавьте его: «Сменить» → ⋯ у шрифта → «Добавить начертание»
        </p>
      ) : null}
      <Label>Регистр заголовков</Label>
      <Segmented aria-label="Регистр заголовков" options={CASE_OPTIONS} value={headingCase} onChange={(next) => onType({ headingCase: next })} />
      <HeadingScaleField value={headingScale} onType={onType} onPreview={onPreview} />
    </div>
  );
}

interface EyebrowProps {
  value: EyebrowFrom;
  /** Разрядка надписей капсом, em. */
  tracking: number;
  /** Шрифт надписей — для образца разрядки. */
  fontKey: string;
  onType: TypePatch;
  onPreview?: TypePatch;
}

/** «Надписи капсом над заголовками: [Основным текстом | Шрифтом заголовков]» и их разрядка. */
export function EyebrowFromField({ value, tracking, fontKey, onType, onPreview }: EyebrowProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label>{EYEBROW_FROM_LABEL}</Label>
      <Segmented aria-label={EYEBROW_FROM_LABEL} options={EYEBROW_OPTIONS} value={value} onChange={(next) => onType({ eyebrowFrom: next })} />
      {value === "body" ? <p className="type-caption text-on-surface-variant">{EYEBROW_FROM_NOTE}</p> : null}
      <EyebrowTrackingField value={tracking} fontKey={fontKey} onType={onType} onPreview={onPreview} />
    </div>
  );
}
